import { HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { HandleErrorService } from '../../interceptors/handle-error.service';
import { ICustomRequestHttp, IRequestNative } from '../../models/request.model';

/**
 * Servicio de red que abstrae la lógica HTTP para web y dispositivos nativos.
 * - Para web usa HttpClient (con interceptores)
 * - Para nativo (Android/iOS) usa cordova-plugin-advanced-http
 * - Aplica encabezado Authorization automáticamente en contexto nativo
 * - Maneja 401/406 con refresh y cierre de sesión (vía handleErrorS)
 */
@Injectable({ providedIn: 'root' })
export class RequestService {

    constructor(
        private readonly handleErrorS: HandleErrorService,
        private readonly httpClient: HttpClient,
    ) { }

    public requestHttp = <T, R = any>(requestData: ICustomRequestHttp<T>): Observable<R> =>
        Capacitor.isNativePlatform() ? this.requestHttpApp(requestData) : this.requestHttpWeb(requestData);

    /**
     * Sends an HTTP request using the provided request data.
     * @param requestData The request data object containing the method, URL, and body.
     * @returns An Observable that emits the HTTP response.
     */
    private requestHttpApp = <T>(requestData: ICustomRequestHttp<T>): Observable<any> => {
        const originalAuth = requestData.init?.headers?.['Authorization'];

        // ? Si Authorization: 'none', quitar el header y no aplicar token
        if (originalAuth === 'none') {
            const headers = Capacitor.getPlatform() === 'ios' && requestData.method === 'get' ? {} : { 'content-type': 'application/json' };
            return this.requestHttpMd(requestData, headers);
        }

        return this.handleErrorS.getToken$().pipe(
            switchMap(token => {
                if (!token) return throwError(() => new Error('Token no disponible'));

                if (!this.handleErrorS.validarJWT(token)) {
                    // Token inválido → Forzar refresh
                    return this.handleErrorS.handle401Error((newToken) => {
                        const retryHeaders = this.retryHeaders(requestData, newToken);
                        return this.requestHttpMd(requestData, retryHeaders);
                    });
                }

                // token válido
                const headers = this.retryHeaders(requestData, token);
                return this.requestHttpMd(requestData, headers).pipe(
                    catchError(error => {
                        if (error instanceof HttpErrorResponse) {
                            return this.handleErrorS.handleHttpStatusError(error, (newToken) => {
                                const retryHeaders = this.retryHeaders(requestData, newToken);
                                return this.requestHttpMd(requestData, retryHeaders);
                            });
                        }
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    private requestHttpMd = <T>(requestData: ICustomRequestHttp<T>, headers: { [key: string]: string; } | {}): Observable<any> => {

        const formData: FormData = new FormData();
        // Detectar si el cuerpo es FormData
        const isFormData = requestData.init?.headers?.['Content-Type']?.includes('multipart/form-data');
        if (isFormData) {
            const dataToSend: any = requestData.body;
            Object.keys(dataToSend).forEach(key => {
                if (key !== 'files') formData.append(key, String(dataToSend[key]));
            });
            dataToSend.files.forEach((imageBlob: Blob, index: number) => {
                formData.append('files', imageBlob, `file_${dataToSend.ID_Bodega}_${dataToSend.NUD}_${index}.jpg`);
            });
        }

        if (isFormData) return this.uploadFormData(requestData, headers, formData);

        return from(
            CapacitorHttp.request({
                url: requestData.url,
                method: requestData.method.toLocaleUpperCase(),
                params: requestData.method === 'get' || requestData.method === 'delete'
                    ? Object.fromEntries(Object.entries(requestData.body || {}).filter(([_, value]) => value != null).map(([key, value]) => [key, String(value)]))
                    : undefined,
                data: requestData.method === 'get' || requestData.method === 'delete' ? {} : requestData.body,
                headers,
                responseType: 'json',
            })
                .then(value => this.responseNative(value, requestData.url))
                .catch(error => this.errorNative(error, requestData.url))
        );
    }

    /**
     * Metodo para envio de formdata para nativo
     */
    private uploadFormData = <T>(requestData: ICustomRequestHttp<T>, headers: { [key: string]: string; }, formData: FormData) => {
        return from(
            fetch(requestData.url, {
                method: requestData.method.toUpperCase(),
                body: formData,
                headers: { Authorization: headers.Authorization },
            })
                .then(async (response) => {
                    const textData = await response.text();
                    let parsedData: T | string;
                    try {
                        parsedData = JSON.parse(textData);
                    } catch {
                        parsedData = textData; // Si no es JSON válido (ej: texto plano o vacío)
                    }

                    // Convertir las cabeceras de fetch en un objeto plano para HttpHeaders
                    const mappedHeaders: { [key: string]: string } = {};
                    response.headers.forEach((val, key) => {
                        mappedHeaders[key] = val;
                    });

                    // Crear el objeto emulado idéntico al que entregaba CapacitorHttp
                    return {
                        status: response.status,
                        statusText: response.statusText,
                        data: parsedData,
                        headers: mappedHeaders,
                        url: response.url
                    };
                })
                .then(value => this.responseNative(value, requestData.url))
                .catch(error => this.errorNative(error, requestData.url))
        );
    }

    private responseNative = (value: IRequestNative, url: string) => {
        if (value.status && (value.status < 200 || value.status >= 300)) {
            const parsed = typeof value.data === 'string' ? JSON.parse(value.data) : value.data;
            throw new HttpErrorResponse({
                error: parsed,
                headers: new HttpHeaders(value.headers || {}),
                status: value.status,
                statusText: (value as any).statusText || 'HTTP Error',
                url: value.url || url || ''
            });
        }

        return value?.data?.result ?? value?.data ?? value;
    }

    private errorNative = (error: any, url: string) => {
        const parsed = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
        const httpError = new HttpErrorResponse({
            error: parsed,
            headers: new HttpHeaders(error.headers || {}),
            status: error.status || 0,
            statusText: error.statusText || 'Unknown Error',
            url: url || ''
        });
        throw httpError;
    }

    /**
     * Verifica si existen headers en la solicitud y los convierte a un objeto HttpHeaders
     * @param requestData 
     * @returns 
     */
    public requestHttpWeb = <T>(requestData: ICustomRequestHttp<T>): Observable<any> => {
        requestData.init ??= {};
        requestData.init.headers = this.normalizeHeaders(requestData.init.headers);
        return this.httpClient.request(
            new HttpRequest<T>(
                requestData.method.toUpperCase(),
                requestData.url,
                requestData.body as T,
                requestData.init
            )
        )
            .pipe(
                filter(resp => resp.hasOwnProperty('type') && resp.type !== 0),
                map((resp: any) => resp?.body?.result ?? resp?.body ?? resp),
            );
    }

    private normalizeHeaders = (headers?: HttpHeaders | { [key: string]: string }): HttpHeaders => {
        if (!headers) return new HttpHeaders();
        return headers instanceof HttpHeaders ? headers : new HttpHeaders(headers);
    }

    private retryHeaders = <T>(requestData: ICustomRequestHttp<T>, token: string) => ({
        Authorization: `Bearer ${token}`,
        ...(Capacitor.getPlatform() === 'ios' && requestData.method.toLocaleLowerCase() === 'get' ? {} : { 'Content-Type': 'application/json', ...requestData.init?.headers })
    });

}
